import React, { useCallback, useEffect, useState } from 'react';
import Button from '~/app/common/Button';
import Icon from '~/app/common/Icon';
import Modal from '~/app/common/Modal';
import useTranslation from '~/app/hooks/useTranslation';
import Field from '~/app/modules/form/Field';
import Text from '~/app/modules/form/Text';
import { useStoreActions, useStoreState } from '~/store';
import { Part } from '~/types';
import Quantity from '~/app/common/Quantity';
import classes from './style.module.scss';
import { PartLinkTypes, PartTypes } from '~/app/data/statuses/issue';
import { getPartStoreKey } from '../../helper';

export interface ManualPartFormProps {
  open: boolean
  onClose: () => void
  part?: Part
  type: PartLinkTypes
  persistOnSave?: boolean
}

type FieldState<T> = {
  value: T;
  touched: boolean;
  dirty: boolean;
};

type PartFormState = {
  article_name: FieldState<string>;
  article_no: FieldState<string>;
  price_per_unit: FieldState<string | number>;
  quantity: FieldState<number>;
};

type FieldName = keyof PartFormState;

const getInitialFormState = (part?: Part): PartFormState => ({
  article_name: { value: part?.article_name ?? '', touched: false, dirty: false },
  article_no: { value: part?.article_no ?? '', touched: false, dirty: false },
  price_per_unit: { value: part?.price_per_unit ?? '', touched: false, dirty: false },
  quantity: { value: part?.quantity ?? 1, touched: false, dirty: false },
});

const isFormValid = (state: PartFormState) =>
  state.article_name.value.trim() &&
  state.article_no.value.trim() &&
  state.price_per_unit.value !== '' &&
  Number(state.price_per_unit.value) > 0 &&
  Number(state.quantity.value) > 0;

const MAX_PRICE = 999999999;

export type ManualPartFormComponent = React.FC<ManualPartFormProps>

const ManualPartForm: ManualPartFormComponent = ({ open, onClose, part, type, persistOnSave = false }) => {

  const basePath = getPartStoreKey(type);
  const id = useStoreState(state => state[basePath].data.id);
  const tempParts = useStoreState(state => state[basePath].tempParts ?? []);
  const setTempPartsAction = useStoreActions(actions => actions[basePath].setTempParts);
  const savePartAction = useStoreActions(actions => actions[basePath].savePart);
  const fetchPartsAction = useStoreActions(actions => actions[basePath].fetchParts);
  const [formState, setFormState] = useState<PartFormState>(getInitialFormState(part));
  const { t } = useTranslation('parts');

  useEffect(() => {
    setFormState(getInitialFormState(part));
  }, [part]);

  const updateField = useCallback((field: FieldName, value: string | number) => {
    setFormState(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        value,
        dirty: value !== (part?.[field] ?? (typeof value === 'number' ? 0 : '')),
      },
    }));
  }, [part, setFormState]);

  const markTouched = useCallback((field: FieldName) => {
    setFormState(prev => ({
      ...prev,
      [field]: { ...prev[field], touched: true },
    }));
  }, [setFormState]);

  const onCloseDialog = useCallback(() => {
    setFormState(getInitialFormState(part));
    onClose();
  }, [part, onClose, setFormState]);

  const handleFormSubmit = useCallback(() => {
    if (!isFormValid(formState)) {
      setFormState(prev =>
        Object.fromEntries(
          Object.entries(prev).map(([key, field]) => [key, { ...field, touched: true }])
        ) as PartFormState
      );
      return;
    }

    const { article_no, article_name, price_per_unit, quantity } = formState;
    const priceNum = Number(price_per_unit.value);

    const updatedParts = [...tempParts];
    const newPartData = {
      article_no: article_no.value,
      article_name: article_name.value,
      price_per_unit: priceNum,
      quantity: quantity.value,
    };

    if (part?.id) {
      const index = updatedParts.findIndex(m => m.id === part.id);
      if (index >= 0) {
        updatedParts[index] = { ...part, ...newPartData };
      } else {
        updatedParts.push({ ...part, ...newPartData, added: true });
      }
    } else {
      updatedParts.push({
        id: `Manual_${article_no.value}${Math.random().toExponential(0)}`,
        ...newPartData,
        type: PartTypes.MANUAL,
        added: true,
        in_stock: Infinity
      });
    }

    setTempPartsAction(updatedParts);

    if (persistOnSave) {
      savePartAction({
        id: String(id),
        parts: updatedParts,
      }).then((res) => {
        if (res.success) {
          fetchPartsAction(String(id));
        }
        onCloseDialog();
      });
      return;
    }

    onCloseDialog();
  }, [fetchPartsAction, formState, id, part, persistOnSave, savePartAction, setTempPartsAction, tempParts, onCloseDialog]);

  const getError = useCallback((name: string, value: any, touched: boolean) => {
    if (!touched) return undefined;
    if (name === 'price_per_unit') {
      return value === '' || Number(value) <= 0 ? ['required'] : undefined;
    }
    return !value.toString().trim() ? ['required'] : undefined;
  }, []);
  const Label = ({ label, required }: { label: string, required: boolean }) => (
    <div>
      {label}
      {required && <span className={classes['required-mark']}>*</span>}
    </div>
  )

  const handleCountInputChange = useCallback((raw: string) => {
    const value = raw.trim();
    if (value === '') {
      updateField('price_per_unit', '');
      return;
    }
    if (!/^\d*\.?\d{0,2}$/.test(value)) {
      return;
    }
    const numericValue = Number(value);
    if (!Number.isNaN(numericValue) && numericValue > MAX_PRICE) {
      return;
    }
    updateField('price_per_unit', value);
  }, [updateField]);

  const handlePriceBlur = useCallback(() => {
    const value = formState.price_per_unit.value;
    if (value === '') {
      markTouched('price_per_unit');
      return;
    }
    const number = Number(value);
    if (Number.isNaN(number)) {
      return;
    }
    updateField('price_per_unit', number.toFixed(2));
    markTouched('price_per_unit');
  }, [markTouched, updateField, formState.price_per_unit.value]);

  return (
    <Modal className={classes['manualPart']} open={open} onClose={onCloseDialog} position={'bottom'}>
      <div className={classes['partManager']}>
        <div className={classes['partsHeader']}>
          <Icon name='arrow_back' onClick={onCloseDialog} />
          <span>{part !== undefined ? t('edit') : t('add')} {t('parts')}</span>
        </div>
        <div className={classes['partForm']}>
          <Text
            name="article_name"
            label={<Label label={t('name')} required={true} />}
            placeholder={t('name')}
            type="text"
            maxLength={255}
            value={formState.article_name.value}
            required
            errors={getError('article_name', formState.article_name.value, formState.article_name.touched)}
            onChange={e => updateField('article_name', e.target.value)}
            onBlur={() => markTouched('article_name')}
          />
          <Text
            name="article_no"
            label={<Label label={t('articleNo')} required={true} />}
            placeholder={t('articleNo')}
            type="text"
            maxLength={50}
            value={formState.article_no.value}
            required
            errors={getError('article_no', formState.article_no.value, formState.article_no.touched)}
            onChange={e => updateField('article_no', e.target.value)}
            onBlur={() => markTouched('article_no')}
          />
          <Text
            name="price_per_unit"
            min={0}
            max={999999999}
            label={<Label label={t('salePrice')} required={true} />}
            placeholder={t('salePrice')}
            type="number"
            value={formState.price_per_unit.value.toString()}
            required
            errors={getError('price_per_unit', formState.price_per_unit.value, formState.price_per_unit.touched)}
            onChange={(e) => handleCountInputChange(e.target.value)}
            onBlur={handlePriceBlur}
          />
          <Field label={<Label label={t('quantity')} required={true} />}>
            <Quantity quantity={formState.quantity.value} quantityChange={q => updateField('quantity', q)} />
          </Field>
          <Button className={classes['addButton']} onClick={handleFormSubmit}>{t('save')}</Button>
        </div>
      </div>
    </Modal>
  )
}

export default ManualPartForm;
